import React, { useEffect, useState } from "react";
import AdminLayout from "./AdminLayout";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Badge } from "../ui/badge";
import api from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, ShieldOff, AlertCircle } from "lucide-react";

export default function AdminPipeline() {
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({ key: "", label: "", blocks_payout: false, order_index: 50, color: "#94A3B8" });

  const load = () => {
    setLoading(true);
    api.get("/admin/pipeline/stages").then((r) => setStages(r.data.stages || [])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!draft.key || !draft.label) { toast.error("Key and label required"); return; }
    try {
      await api.post("/admin/pipeline/stages", draft);
      toast.success("Stage created");
      setOpen(false);
      setDraft({ key: "", label: "", blocks_payout: false, order_index: 50, color: "#94A3B8" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
  };

  const togglePayout = async (s, v) => {
    try {
      await api.patch(`/admin/pipeline/stages/${s.id}`, { blocks_payout: v });
      toast.success(`Payout block ${v ? "enabled" : "disabled"} on ${s.label}`);
      load();
    } catch (e) { toast.error("Update failed"); }
  };

  const del = async (s) => {
    if (!window.confirm(`Delete "${s.label}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/pipeline/stages/${s.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <AdminLayout
      title="Pipeline"
      subtitle="Custom order stages staff can apply. Stages with payout-block freeze leg-2 sends."
      actions={
        <Button onClick={() => setOpen(true)} size="sm"
                className="bg-primary hover:bg-primary/90 text-white rounded-xl gap-1.5"
                data-testid="add-stage-btn">
          <Plus className="w-4 h-4" /> Add stage
        </Button>
      }
    >
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3 mb-5 flex items-start gap-2.5 text-sm" data-testid="pipeline-warning">
        <AlertCircle className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
        <div className="text-amber-900">
          Toggling <strong>payout-block ON</strong> means orders tagged with this stage <strong>cannot have leg-2 fired</strong>.
          Use for AML / SoF / fraud holds. Toggling OFF restores normal flow.
        </div>
      </div>

      <div className="xch-card overflow-hidden" data-testid="pipeline-stages">
        {loading && <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>}
        {!loading && stages.length === 0 && (
          <div className="p-8 text-center text-sm text-muted-foreground">No custom stages yet.</div>
        )}
        {!loading && stages.length > 0 && (
          <div className="divide-y divide-border">
            {stages.map((s) => (
              <div key={s.id} className="p-4 flex items-center gap-3" data-testid={`stage-${s.key}`}>
                <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: s.color || "#94A3B8" }} />
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-foreground flex items-center gap-2 flex-wrap">
                    {s.label}
                    <Badge variant="outline" className="text-[9px] uppercase font-mono">{s.key}</Badge>
                    {s.blocks_payout && (
                      <span className="flex items-center gap-1 text-[9px] uppercase tracking-wider font-bold bg-rose-50 text-rose-700 border border-rose-200 px-1.5 py-0.5 rounded-full">
                        <ShieldOff className="w-2.5 h-2.5" /> Blocks payout
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-muted-foreground tabular-nums">order #{s.order_index}</div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">Block payout</span>
                    <Switch checked={s.blocks_payout} onCheckedChange={(v) => togglePayout(s, v)}
                            data-testid={`toggle-${s.key}`} />
                  </div>
                  <Button onClick={() => del(s)} variant="ghost" size="sm"
                          className="text-rose-700 hover:bg-rose-50 rounded-lg"
                          data-testid={`delete-${s.key}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md rounded-3xl" data-testid="add-stage-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Add custom stage</DialogTitle>
            <DialogDescription className="text-xs">
              Define a new operator pipeline stage. Toggle "blocks payout" to gate the XGate handler on this stage.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Field label="Key (lowercase_snake)">
              <Input value={draft.key} onChange={(e) => setDraft({ ...draft, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") })}
                     className="rounded-xl font-mono" data-testid="stage-key-input" />
            </Field>
            <Field label="Label (shown to staff)">
              <Input value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                     className="rounded-xl" data-testid="stage-label-input" />
            </Field>
            <Field label="Order index">
              <Input type="number" value={draft.order_index}
                     onChange={(e) => setDraft({ ...draft, order_index: parseInt(e.target.value || 50, 10) })}
                     className="rounded-xl font-mono" data-testid="stage-order-input" />
            </Field>
            <Field label="Color">
              <Input type="color" value={draft.color}
                     onChange={(e) => setDraft({ ...draft, color: e.target.value })}
                     className="rounded-xl h-10 w-20" data-testid="stage-color-input" />
            </Field>
            <label className="flex items-start gap-2 text-sm pt-1">
              <Switch checked={draft.blocks_payout}
                      onCheckedChange={(v) => setDraft({ ...draft, blocks_payout: v })}
                      data-testid="stage-blocks-payout" />
              <div>
                <div className="font-semibold text-foreground">Blocks payout</div>
                <div className="text-xs text-muted-foreground">Tagging an order with this stage will freeze leg-2 sends.</div>
              </div>
            </label>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} className="bg-primary hover:bg-primary/90 text-white rounded-xl" data-testid="confirm-stage-btn">
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{label}</Label>
    {children}
  </div>
);
